import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore, useUIStore } from '../stores';
import { API_URL, formatCurrency } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Plus, Building2, Trash2, ArrowRightLeft, Loader2,
  CalendarClock, Eye, EyeOff, AlertTriangle, Clock,
  Paperclip, X, Repeat, // Pour le compteur
  History, // Pour la date
  PlayCircle, // Pour indiquer que c'est actif
  StopCircle, // Pour indiquer que c'est arrêté
  Send
} from 'lucide-react';

const SOURCES = [
  { value: 'website', label: 'Site web' },
  { value: 'referral', label: 'Recommandation' },
  { value: 'social', label: 'Réseaux sociaux' },
  { value: 'event', label: 'Salon / Événement' },
  { value: 'cold_call', label: 'Démarchage' },
  { value: 'other', label: 'Autre' },
];

const DEFAULT_COLUMNS = [
  { id: 'new', name: 'Nouveau', color: '#3b82f6' },
  { id: 'won', name: 'Gagné', color: '#22c55e' },
];

// --- COMPOSANT CARTE PROSPECT ---
// Dans src/pages/LeadsPage.js

const LeadCard = ({ lead, index, onEdit, onDelete, onManualRelance, showFollowup }) => {
  const leadId = lead.id || lead._id;

  // Calcul simple pour la couleur de la bordure gauche (Indicateur d'urgence)
  const getBorderClass = () => {
    if (!lead.next_followup_date) return "border-l-transparent"; // Pas de date prévue
    try {
      const today = new Date();
      const next = new Date(lead.next_followup_date);
      today.setHours(0, 0, 0, 0); next.setHours(0, 0, 0, 0); // Comparer les dates sans les heures

      if (next < today) return "border-l-red-500"; // En retard (Rouge)
      if (next.getTime() === today.getTime()) return "border-l-green-500"; // Aujourd'hui (Vert)
      return "border-l-blue-200"; // Futur (Bleu clair)
    } catch (e) { return "border-l-transparent"; }
  };

  const borderClass = showFollowup ? getBorderClass() : "border-l-transparent";

  return (
    <Draggable draggableId={leadId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`mb-2 transition-transform ${snapshot.isDragging ? 'z-50' : ''}`}
          style={{ ...provided.draggableProps.style }}
        >
          {/* On ajoute 'border-l-4' et la couleur calculée.
              Cela remplace le badge par une fine ligne colorée à gauche.
          */}
          <Card className={`relative cursor-grab active:cursor-grabbing select-none group hover:shadow-md border-l-4 ${borderClass} ${snapshot.isDragging ? 'shadow-xl ring-2 ring-primary/30' : ''}`}>

            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">

                {/* --- ZONE PRINCIPALE (Gauche) --- */}
                <div className="flex-1 min-w-0" onClick={() => onEdit(lead)}>
                  <p className="font-medium text-sm truncate pr-2">{lead.name}</p>

                  {lead.company && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Building2 className="w-3 h-3" /> {lead.company}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-1 mt-2">
                    {lead.estimated_value > 0 && (
                      <span className="text-xs font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                        {formatCurrency(lead.estimated_value)}
                      </span>
                    )}
                    {lead.source && (
                      <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground h-5">
                        {lead.source}
                      </Badge>
                    )}
                  </div>

                  {/* --- STATS RELANCES (En bas) --- */}
                  {(lead.followup_count > 0 || lead.auto_followup_active) && (
                    <div className="mt-3 pt-2 border-t border-dashed flex items-center justify-between text-[10px] text-muted-foreground">

                      {/* Statut Auto */}
                      <div className="flex items-center gap-1">
                        {lead.auto_followup_active ? (
                          <span className="text-blue-600 bg-blue-50 px-1 rounded flex items-center"><PlayCircle className="w-3 h-3 mr-1" /> Auto</span>
                        ) : lead.followup_count > 0 ? (
                          <span className="text-gray-400 flex items-center"><StopCircle className="w-3 h-3 mr-1" /> Stop</span>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2">
                        {lead.followup_count > 0 && (
                          <span className="flex items-center" title="Nb Relances"><Repeat className="w-3 h-3 mr-1" /> {lead.followup_count}</span>
                        )}
                        {lead.last_followup_date && (
                          <span className="flex items-center" title="Dernière relance"><History className="w-3 h-3 mr-1" /> {new Date(lead.last_followup_date).toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost" size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); onDelete(leadId); }}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>

                  <Button
                    variant="ghost" size="icon"
                    className={`h-6 w-6 ${!lead.email ? 'text-gray-300 cursor-not-allowed' : 'text-blue-400 hover:text-blue-600 hover:bg-blue-50'}`}
                    title={lead.email ? "Relancer maintenant" : "Pas d'email"}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (lead.email) onManualRelance(leadId);
                    }}
                  >
                    <Send className="w-3 h-3" />
                  </Button>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

// --- COMPOSANT COLONNE KANBAN ---
const KanbanColumn = ({ column, leads, onEdit, onDelete, onManualRelance, showFollowup, isOrphan }) => {
  const total = leads.reduce((s, lead) => s + (lead.estimated_value || 0), 0);

  return (
    <div className="flex-1 min-w-[280px] max-w-[320px]" data-testid={`kanban-column-${column.id}`}>
      <div className={`flex items-center gap-2 mb-3 ${isOrphan ? 'text-red-600' : ''}`}>
        {isOrphan ? (
          <AlertTriangle className="w-4 h-4" />
        ) : (
          <div
            className="w-2.5 h-2.5 rounded-full shadow-sm"
            style={{ backgroundColor: column.color }}
          />
        )}
        <h3 className="text-sm font-medium truncate" title={column.name}>{column.name}</h3>
        <Badge variant={isOrphan ? "destructive" : "secondary"} className="text-xs ml-auto">
          {leads.length}
        </Badge>
      </div>

      {total > 0 && (
        <p className="text-xs text-muted-foreground mb-2">{formatCurrency(total)}</p>
      )}

      <Droppable droppableId={column.id}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`
                min-h-[120px] p-1.5 rounded-lg transition-colors duration-150 border-2 border-dashed
                ${snapshot.isDraggingOver ? 'bg-primary/5 border-primary/40' : 'bg-secondary/20 border-transparent'}
                ${isOrphan ? 'bg-red-50/50 border-red-200' : ''}
            `}
          >
            {leads.map((lead, index) => (
              <LeadCard
                key={lead.id || lead._id}
                lead={lead}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
                onManualRelance={onManualRelance}
                showFollowup={showFollowup}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// --- DIALOGUE LEAD ---
const LeadFormDialog = ({ lead, open, onOpenChange, onSave, columns }) => {
  const defaultStatus = columns && columns.length > 0 ? columns[0].id : '';

  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '', source: 'none',
    estimated_value: '', notes: '', status: '',
    auto_followup_active: false, followup_frequency_days: 7, max_followups: 3
  });

  useEffect(() => {
    if (open) {
      if (lead) {
        setForm({
          ...lead,
          source: lead.source || 'none',
          estimated_value: lead.estimated_value?.toString() || '',
          auto_followup_active: lead.auto_followup_active || false,
          followup_frequency_days: lead.followup_frequency_days || 7,
          max_followups: lead.max_followups || 3,
          status: lead.status || defaultStatus,
          notes: '' // Reset notes pour éviter conflit de type
        });
      } else {
        setForm({
          name: '', company: '', email: '', phone: '', source: 'none',
          estimated_value: '', notes: '', status: defaultStatus,
          auto_followup_active: false, followup_frequency_days: 7, max_followups: 3
        });
      }
    }
  }, [lead, open, columns, defaultStatus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      name: form.name,
      company: form.company || null,
      email: form.email || null,
      phone: form.phone || null,
      source: form.source === 'none' ? null : form.source,
      estimated_value: parseFloat(form.estimated_value) || 0,
      // On envoie le statut, l'automatisation, etc.
      status: form.status,
      auto_followup_active: form.auto_followup_active,
      followup_frequency_days: parseInt(form.followup_frequency_days) || 7,
      max_followups: parseInt(form.max_followups) || 3,
      // Notes : si vide, on envoie null pour ne rien ajouter
      notes: form.notes && form.notes.trim() !== '' ? form.notes : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead ? 'Modifier le lead' : 'Nouveau lead'}</DialogTitle>
          <DialogDescription>Informations du prospect</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Nom / Contact *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Entreprise</Label><Input value={form.company || ''} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Téléphone</Label><Input value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Source</Label>
              <Select value={form.source || 'none'} onValueChange={v => setForm({ ...form, source: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non définie</SelectItem>
                  {SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Valeur estimée (€)</Label><Input type="number" step="0.01" value={form.estimated_value} onChange={e => setForm({ ...form, estimated_value: e.target.value })} /></div>
          </div>
          <div className="space-y-2">
            <Label>Étape du pipeline</Label>
            <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {columns && columns.map(col => (
                  <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                ))}
                {!columns.find(c => c.id === form.status) && form.status && (
                  <SelectItem value={form.status} disabled>⚠️ Actuel: {form.status}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-4 border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5"><Label className="text-base">Relance Automatique</Label><p className="text-xs text-muted-foreground">Rappel si aucune action.</p></div>
              <Switch checked={form.auto_followup_active} onCheckedChange={(c) => setForm({ ...form, auto_followup_active: c })} />
            </div>
            {form.auto_followup_active && (
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Fréquence (jours)</Label>
                  <Input type="number" min="1" value={form.followup_frequency_days} onChange={(e) => setForm({ ...form, followup_frequency_days: e.target.value })} />
                </div>
                <div className="flex-1">
                  <Label>Max Relances</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={form.max_followups || 4}
                    onChange={(e) => setForm({ ...form, max_followups: parseInt(e.target.value) || 4 })}
                    title="L'automatisation s'arrêtera après ce nombre d'emails"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Ajouter une Note</Label>
            <Textarea
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              placeholder="Nouvelle note..."
              rows={2}
            />
            {/* Historique Notes */}
            {lead && lead.notes && lead.notes.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                <p className="font-semibold mb-1">Historique :</p>
                <div className="max-h-24 overflow-y-auto space-y-1 pl-1">
                  {lead.notes.map((n, i) => (
                    <div key={i} className="border-l-2 pl-2 border-muted">
                      <span className="font-medium text-[10px]">{new Date(n.created_at).toLocaleDateString()}</span>
                      <span className="block">{n.content}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit">{lead ? 'Enregistrer' : 'Créer'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- DIALOGUE CONVERT ---
const ConvertDialog = ({ lead, open, onOpenChange, onConfirm, loading }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle className="font-heading flex items-center gap-2"><ArrowRightLeft className="w-5 h-5" /> Convertir en client</DialogTitle>
        <DialogDescription>Le lead <strong>{lead?.name}</strong> sera créé comme client.</DialogDescription>
      </DialogHeader>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
        <Button onClick={onConfirm} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Créer le client
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

// --- COMPOSANT DIALOGUE DE RELANCE LEAD ---
const LeadFollowUpDialog = ({ lead, open, onOpenChange }) => {
  const { token } = useAuthStore();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Charger le template à l'ouverture
  useEffect(() => {
    if (open && lead) {
      setFiles([]);
      // Récupération du template depuis le backend
      fetch(`${API_URL}/leads/${lead.id || lead._id}/follow-up-template`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        setSubject(data.subject || '');
        setBody(data.body || '');
      })
      .catch(err => {
          console.error("Erreur template", err);
          toast.error("Impossible de charger le modèle d'email.");
      });
    }
  }, [open, lead, token]);

  const handleSend = async () => {
    if (!subject || !body) return toast.error('Sujet et message requis');

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', body);
      files.forEach(f => formData.append('files', f));

      // Envoi de la relance manuelle via la route modifiée
      const res = await fetch(`${API_URL}/leads/${lead.id || lead._id}/trigger-followup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }, // Pas de Content-Type pour FormData !
        body: formData,
      });

      if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.detail || 'Erreur lors de l\'envoi');
      }

      toast.success('🚀 Relance envoyée avec succès !');
      onOpenChange(false); // Fermer la modale
    } catch (error) {
      toast.error("Erreur technique : " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Relancer {lead.name}</DialogTitle>
          <DialogDescription>Personnalisez le message avant l'envoi.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Email destinataire */}
          <div className="text-sm text-muted-foreground">
             À : <span className="font-medium text-foreground">{lead.email}</span>
          </div>

          {/* Sujet */}
          <div className="space-y-1">
            <Label>Sujet</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} />
          </div>

          {/* Corps */}
          <div className="space-y-1">
            <Label>Message</Label>
            <Textarea
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={8}
                className="bg-muted/20"
            />
          </div>

          {/* Pièces jointes */}
          <div className="space-y-2">
            <Label>Pièces jointes</Label>
            <div className="flex items-center gap-2">
                <Input
                    id="lead-file-upload"
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => e.target.files && setFiles([...files, ...Array.from(e.target.files)])}
                />
                <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById('lead-file-upload').click()}>
                    <Paperclip className="w-4 h-4 mr-2" /> Joindre fichiers
                </Button>
                <span className="text-xs text-muted-foreground">
                    {files.length > 0 ? `${files.length} fichier(s)` : 'Aucun fichier'}
                </span>
            </div>
            {/* Liste des fichiers */}
            {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {files.map((f, i) => (
                        <Badge key={i} variant="secondary" className="pl-2 pr-1">
                            {f.name} <X className="w-3 h-3 ml-1 cursor-pointer hover:text-red-500" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} />
                        </Badge>
                    ))}
                </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={handleSend} disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <Send className="w-4 h-4 mr-2"/>}
                Envoyer
            </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- PAGE PRINCIPALE ---
// --- PAGE PRINCIPALE ---
const LeadsPage = () => {
  const { showMetrics, toggleMetrics, showFollowupDate, toggleFollowupDate } = useUIStore();
  const { token } = useAuthStore();
  const queryClient = useQueryClient();

  // États existants
  const [formOpen, setFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [convertLead, setConvertLead] = useState(null);
  const [isDndReady, setIsDndReady] = useState(false);

  // --- NOUVEAUX ÉTATS (Pour la relance manuelle) ---
  const [followUpLead, setFollowUpLead] = useState(null);
  const [isFollowUpOpen, setIsFollowUpOpen] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setIsDndReady(true));
    return () => cancelAnimationFrame(animation);
  }, []);

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['company-settings'],
    queryFn: async () => (await fetch(`${API_URL}/settings/company`, { headers: { Authorization: `Bearer ${token}` } })).json()
  });

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['leads'],
    queryFn: async () => (await fetch(`${API_URL}/leads`, { headers: { Authorization: `Bearer ${token}` } })).json()
  });

  const { pipelineColumns, columnLeads, displayColumns, wonValue, totalValue, convRate } = useMemo(() => {
    const pipelineColumns = settings?.pipeline_stages || [];
    const validStageIds = pipelineColumns.map(c => c.id);
    const orphanLeads = leads.filter(l => !validStageIds.includes(l.status));

    const displayColumns = [...pipelineColumns];
    if (orphanLeads.length > 0) {
      displayColumns.unshift({ id: 'unclassified', name: `⚠️ Non classés (${orphanLeads.length})`, color: '#94a3b8' });
    }

    const columnLeads = {};
    const safeColumns = orphanLeads.length > 0 ? [{ id: 'unclassified' }, ...pipelineColumns] : pipelineColumns;

    safeColumns.forEach(col => {
      columnLeads[col.id] = leads.filter(ld => ld.status === col.id);
    });

    const totalValue = leads.reduce((s, ld) => s + (ld.estimated_value || 0), 0);
    const wonStatusId = pipelineColumns.find(s => s.is_system && (s.id === 'won' || s.id.includes('won')))?.id || 'won';
    const wonValue = leads.filter(ld => ld.status === wonStatusId).reduce((s, ld) => s + (ld.estimated_value || 0), 0);
    const convRate = leads.length > 0 ? Math.round(leads.filter(ld => ld.status === wonStatusId).length / leads.length * 100) : 0;

    return { pipelineColumns, columnLeads, displayColumns, wonValue, totalValue, convRate };
  }, [leads, settings]);

  const createMutation = useMutation({
    mutationFn: async (data) => (await fetch(`${API_URL}/leads`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) })).json(),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead créé'); setFormOpen(false); },
    onError: (e) => toast.error(e.message || "Erreur création")
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => (await fetch(`${API_URL}/leads/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(data) })).json(),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead mis à jour'); setFormOpen(false); },
    onError: () => toast.error('Erreur mise à jour')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await fetch(`${API_URL}/leads/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['leads'] }); toast.success('Lead supprimé'); }
  });

  const convertMutation = useMutation({
    mutationFn: async (id) => (await fetch(`${API_URL}/leads/${id}/convert`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })).json(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success(`Client "${data.client?.name}" créé`);
      setConvertLead(null);
    },
    onError: (err) => toast.error(err.message)
  });

  // --- HANDLERS ---
  const handleSave = (data) => {
    if (editingLead) {
      const leadId = editingLead.id || editingLead._id;
      updateMutation.mutate({ id: leadId, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Supprimer ce lead ?')) deleteMutation.mutate(id);
  };

  // --- NOUVEAU HANDLER POUR OUVRIR LE DIALOGUE DE RELANCE ---
  const openRelanceModal = (leadId) => {
    const leadToRelance = leads.find(l => (l.id || l._id) === leadId);
    if (leadToRelance) {
        if (!leadToRelance.email) {
            toast.error("Veuillez ajouter une adresse email à ce lead avant de le relancer.");
            return;
        }
        setFollowUpLead(leadToRelance);
        setIsFollowUpOpen(true);
    }
  };

  const handleDragEnd = (result) => {
    const { draggableId, destination, source } = result;
    if (!destination || destination.droppableId === source.droppableId) return;

    let targetStatus = destination.droppableId;
    if (targetStatus === 'unclassified') return;

    const lead = leads.find(ld => (ld.id === draggableId || ld._id === draggableId));
    if (!lead) return;
    const leadId = lead.id || lead._id;

    const isWonColumn = pipelineColumns.find(s => s.id === targetStatus)?.is_system && (targetStatus === 'won' || targetStatus.includes('won'));
    if (isWonColumn && !lead.converted_client_id) {
      updateMutation.mutate({ id: leadId, data: { status: targetStatus } });
      setTimeout(() => setConvertLead({ ...lead, status: targetStatus }), 300);
      return;
    }
    updateMutation.mutate({ id: leadId, data: { status: targetStatus } });
  };

  if (settingsLoading) return <div className="space-y-6"><div className="flex justify-between"><Skeleton className="h-10 w-48" /><Skeleton className="h-10 w-32" /></div><div className="flex gap-4 overflow-x-auto"><Skeleton className="h-96 w-64 rounded-lg" /></div></div>;

  return (
    <div className="space-y-6 h-[calc(100vh-100px)] flex flex-col" data-testid="leads-page">
      {/* HEADER + BOUTONS */}
      <div className="flex justify-between items-center shrink-0">
        <div>
          <h1 className="text-2xl font-bold font-heading">Prospection</h1>
          <p className="text-muted-foreground">Pipeline commercial</p>
        </div>
        <div className="flex gap-2">
          <Button variant={showFollowupDate ? "secondary" : "ghost"} size="sm" onClick={toggleFollowupDate}>
            <CalendarClock className={`w-4 h-4 mr-2 ${showFollowupDate ? 'text-primary' : 'text-muted-foreground'}`} />
            <span className="hidden sm:inline">Relances</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMetrics}
            title={showMetrics ? "Masquer les chiffres" : "Afficher les statistiques"}
          >
            {showMetrics ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
          </Button>
          <Button onClick={() => { setEditingLead(null); setFormOpen(true); }} disabled={settingsLoading}>
            <Plus className="w-4 h-4 mr-2" /> Nouveau lead
          </Button>
        </div>
      </div>

      {/* METRICS DASHBOARD (C'est ici que showMetrics est utilisé) */}
      {showMetrics ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0 animate-in fade-in slide-in-from-top-2 duration-300">
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total leads</p><p className="text-xl font-bold">{leads.length}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Pipeline</p><p className="text-xl font-bold">{formatCurrency(totalValue)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Gagné</p><p className="text-xl font-bold text-emerald-500">{formatCurrency(wonValue)}</p></CardContent></Card>
          <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Conversion</p><p className="text-xl font-bold">{convRate}%</p></CardContent></Card>
        </div>
      ) : (
        <div className="shrink-0 mb-4">
          <div className="p-6 rounded-xl border border-dashed border-primary/20 bg-primary/5 text-center flex flex-col items-center justify-center gap-2 animate-in fade-in zoom-in-95 duration-300">
            <span className="text-2xl">🎯</span>
            <h3 className="font-heading font-semibold text-lg text-primary">Mode Focus Activé</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Oubliez les chiffres pour l'instant. Concentrez-vous uniquement sur la qualité de vos échanges !
            </p>
          </div>
        </div>
      )}

      {/* LÉGENDE DES COULEURS */}
      <div className="flex items-center gap-6 mb-4 px-2 text-xs text-muted-foreground bg-muted/30 py-2 rounded-md w-fit">
        <span className="font-semibold mr-2">Codes couleur :</span>
        <div className="flex items-center gap-2" title="La date de relance est passée">
          <div className="h-4 w-3 bg-white border-l-4 border-red-500 shadow-sm rounded-sm"></div>
          <span>En retard</span>
        </div>
        <div className="flex items-center gap-2" title="Relance prévue aujourd'hui">
          <div className="h-4 w-3 bg-white border-l-4 border-green-500 shadow-sm rounded-sm"></div>
          <span>Aujourd'hui</span>
        </div>
        <div className="flex items-center gap-2" title="Relance prévue plus tard">
          <div className="h-4 w-3 bg-white border-l-4 border-blue-200 shadow-sm rounded-sm"></div>
          <span>Futur</span>
        </div>
        <div className="flex items-center gap-2" title="Aucune relance programmée">
          <div className="h-4 w-3 bg-white border-l-4 border-gray-100 shadow-sm rounded-sm"></div>
          <span className="opacity-50">Pas de date</span>
        </div>
      </div>

      {/* KANBAN BOARD */}
      {leadsLoading || !isDndReady ? <Skeleton className="flex-1 w-full rounded-lg" /> : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4">
            {displayColumns.map(col => (
              <KanbanColumn
                key={col.id}
                column={col}
                leads={columnLeads[col.id] || []}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onManualRelance={openRelanceModal} // Ici on utilise la fonction locale
                showFollowup={showFollowupDate}
                isOrphan={col.id === 'unclassified'}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      <LeadFormDialog lead={editingLead} open={formOpen} onOpenChange={(o) => { setFormOpen(o); if (!o) setEditingLead(null); }} onSave={handleSave} columns={pipelineColumns} />
      <ConvertDialog lead={convertLead} open={!!convertLead} onOpenChange={(o) => { if (!o) setConvertLead(null); }} onConfirm={() => convertMutation.mutate(convertLead.id || convertLead._id)} loading={convertMutation.isPending} />
      
      {/* NOUVELLE MODALE DE RELANCE */}
      <LeadFollowUpDialog 
          lead={followUpLead} 
          open={isFollowUpOpen} 
          onOpenChange={(open) => {
              setIsFollowUpOpen(open);
              if (!open) {
                  setFollowUpLead(null);
                  queryClient.invalidateQueries({ queryKey: ['leads'] });
              }
          }} 
      />
    </div>
  );
};

export default LeadsPage;